import { SelectedSlotRow } from '@frontend/components/Calendar/BookingsListView/CalendarSlotTable/hooks'
import { CollapsePanelHeaderState } from '@frontend/stores/CollapsePanelStateStore'
import { Dayjs } from 'dayjs'

import { InboundOutboundType } from '@shared/const'

import { AssignShipmentToSlotForm, UISlotListFilterParams } from '@shared-types/calendar'
import { Carrier, Facility } from '@shared-types/domain'
import {
  EnhancedAttachmentV2,
  ShipmentPurchaseOrder,
  ShipmentUpdatePayload,
  ShippingUnitDetail,
} from '@shared-types/shipments'
import { Identifier, Nullable } from '@shared-types/util'

export type FieldError = {
  column: string
  error: string
}

export type Party = {
  company_id: string
  company_name: string
  external_company_id?: string
}

export type CreateShipmentFormStore = {
  sender: {
    company: Nullable<Party>
    facility: Nullable<Facility>
    address: string
  }
  receiver: {
    company: Nullable<Party>
    facility: Nullable<Facility>
    address: string
  }
  planned_shipping_units: ShippingUnitDetail[]
  shipment_purchase_orders: ShipmentPurchaseOrderFormCreationPayload[]
  attachments_v2: EnhancedAttachmentV2[]
  carrier: Nullable<Carrier>
  external_id: Nullable<string>
  tracking_number: Nullable<string>
  import_entry_number: Nullable<string>
  notes: Nullable<string>
  type: InboundOutboundType
  department: Nullable<Identifier>
}

export type BookingSecretDataInfoForm = {
  name: string
  email: string
  phone: string
}

export type BookingSecretTransportationInfoForm = {
  carrier_name: string
  truck_type: string
  license_plate: string
  trailer_plate_no: string
  tracking_number: Nullable<string>
}

export enum BookingPortalStep {
  USER_DETAILS_1 = 0,
  CARRIER_DETAILS_2 = 1,
  DELIVERY_DATE_3 = 2,
  CREATED_BOOKING_4 = 3,
}

export type BookingSecretStoreData = {
  selectedDate: Nullable<Dayjs>
  formData: AssignShipmentToSlotForm
  currentStep: BookingPortalStep
  overrideBookingMinLeadTimeHours: Nullable<number>
}

export type CalendarViewMode = 'list' | 'month'
export type CalendarSelectedShipment = Nullable<{ nid: string; _id: string }>

export enum SlotRowSelectionType {
  ALL = 'all',
  SLOT = 'slot',
  SHIPMENT = 'shipment',
}

export type CalendarBookingStoreData = {
  isBookingDetailsPopupOpen: boolean
  selectedBookingId: string
  initialSelectedShipment: CalendarSelectedShipment
  showCreateShipmentForm: boolean
  facility: Nullable<Facility>
  filters: UISlotListFilterParams
  viewMode: CalendarViewMode
  selectedDate: Dayjs
  selectedMonth: Dayjs
  initialPurchaseOrderId: Nullable<Identifier>
  searchTerm: string
  selectedSlotRows: SelectedSlotRow[]
  selectedSlotRowIds: string[]
  slotSelectionType: SlotRowSelectionType
}

export type ShipmentFormStep1 = {
  shipping_from_company: Identifier
  facility_shipping_from: Identifier
  facility_collection_shipment_address: string
  shipping_to_company: Identifier
  facility_receiving_shipment: Identifier
  facility_receiving_shipment_address: string
  type?: InboundOutboundType
  department?: Nullable<Identifier>
}

export type CollapsePanelStateStoreData = {
  sections: {
    sender_receiver_details: {
      state: CollapsePanelHeaderState
    }
    transportation_details: {
      state: CollapsePanelHeaderState
    }
    shipment_manifest: {
      state: CollapsePanelHeaderState
    }
    planned_shipping_units: {
      state: CollapsePanelHeaderState
    }
  }
  activeCollapse: string | string[]
}

export type ShipmentPurchaseOrderFormCreationPayload = Omit<
  ShipmentPurchaseOrder,
  | 'status'
  | 'status_last_updated_by'
  | 'no_of_lines_shipped'
  | 'no_of_items_shipped'
  | 'no_of_lines_ordered'
  | 'no_of_items_ordered'
  | 'createdAt'
  | 'updatedAt'
> & {
  status?: ShipmentPurchaseOrder['status']
}

export type TransportationUpdateFormValues = Pick<
  ShipmentUpdatePayload,
  'carrier' | 'tracking_number' | 'import_entry_number' | 'external_id'
> & {
  date_of_birth?: Dayjs
}

export type ValueLabel = {
  value: string
  label: string
}
